/**
 * The kinds of building the site sells plans for.
 *
 * One entry per plan type, and everything that lists them reads this: the landing
 * page's cards, the header bar, the sitemap and the glossary's tags. Sheds are the only
 * type today. A second (a deck, a coop) is an entry here with its own folder — its
 * catalog, its configurator and its reference beside the shed's — and a `PlanType`
 * value the glossary can tag its terms with.
 */

import {
  SHED_CATALOG_PATH,
  SHED_DESIGNER_PATH,
  SHED_FILM_PATH,
  SHED_FLOOR_HOW_TO_PATH,
  SHED_REFERENCE_PATH,
  SHED_WORKSHOP_FILM_PATH,
} from "./paths.js";

export type PlanType = "shed";

export interface PlanTypeInfo {
  id: PlanType;
  /** What the header and the landing page call the section: "Sheds". */
  name: string;
  /** One of them, in a sentence: "shed". */
  singular: string;
  /** What its card on the landing page says, in one sentence. */
  tagline: string;
  catalogPath: string;
  designerPath: string;
  referencePath: string;
  /**
   * Its films (`film/films.ts`), by address: each a page of its own and an HTML entry in
   * `vite.config.ts`. The first is the one the landing page links. A new plan type may
   * ship before its first film does, with none.
   */
  filmPaths: readonly string[];
  /**
   * Its how-tos (`film/films.ts`, of kind `how-to`): drawn like the films and played by
   * the same page, each teaching one technique. Listed apart, because a how-to is not one
   * of the thirty second films the landing page offers.
   */
  howToPaths: readonly string[];
}

export const PLAN_TYPES: readonly PlanTypeInfo[] = [
  {
    id: "shed",
    name: "Sheds",
    singular: "shed",
    tagline:
      "Backyard sheds to your own dimensions: a gable, lean-to or saltbox roof, on skids, " +
      "piers or a slab, with the doors and windows where you want them.",
    catalogPath: SHED_CATALOG_PATH,
    designerPath: SHED_DESIGNER_PATH,
    referencePath: SHED_REFERENCE_PATH,
    filmPaths: [SHED_FILM_PATH, SHED_WORKSHOP_FILM_PATH],
    howToPaths: [SHED_FLOOR_HOW_TO_PATH],
  },
];

export function planType(id: PlanType): PlanTypeInfo {
  const found = PLAN_TYPES.find((type) => type.id === id);
  if (found === undefined) {
    throw new Error(`no plan type ${id}`);
  }
  return found;
}
